import { useCallback, useRef, useState } from 'react';
import { recipeV2Client } from '../../../app/services/api/recipeV2';
import { RecipeV2 } from '../types';

/**
 * Recipe Bank is a read-only catalogue of the production (Active) Recipe V2 catalogue -
 * it always queries the dedicated recipev2/recipes/bank endpoint, so legacy/draft/scheduled/
 * expired/archived recipes never surface here, and rows already include a resolved image URL.
 */
const useRecipeBankList = () => {
  const [recipes, setRecipes] = useState<RecipeV2[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const loadRecipes = useCallback(async (
    search: string,
    category: string,
    dietaryAttributeIds: number[],
    page: number,
    pageSize: number,
  ) => {
    const currentRequestId = ++requestId.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await recipeV2Client.getRecipeBankList(
        search,
        category,
        dietaryAttributeIds,
        page + 1,
        pageSize,
      );
      if (currentRequestId !== requestId.current) return;
      setRecipes(response.data);
      setTotalCount(response.totalCount);
    } catch (err) {
      if (currentRequestId !== requestId.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load the recipe bank');
      setRecipes([]);
      setTotalCount(0);
    } finally {
      if (currentRequestId === requestId.current) setIsLoading(false);
    }
  }, []);

  return { recipes, totalCount, isLoading, error, loadRecipes };
};

export default useRecipeBankList;
